// src/pages/FacultyDashboard.tsx
import React from "react";
import { auth, db, fs } from "../firebase";
import { ref, get, child, update } from "firebase/database";
import { useLocation, useNavigate, Link } from "react-router-dom";

/* === Firestore (delete Scheduler bookings) === */
import {
  collection,
  getDocs,
  writeBatch,
  doc as fsDoc,
  DocumentData,
} from "firebase/firestore";

/* === Auth helper for account deletion === */
import { deleteUser } from "firebase/auth";

type StudentRow = {
  uid: string;
  userName?: string;
  userEmail?: string;
};

/* ---------- Tiny inline SVG icon set (no dependency) ---------- */
type IconProps = {
  size?: number;
  strokeWidth?: number;
  style?: React.CSSProperties;
};
const Svg: React.FC<
  IconProps & { children: React.ReactNode; viewBox?: string }
> = ({
  size = 16,
  strokeWidth = 1.8,
  style,
  viewBox = "0 0 24 24",
  children,
}) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "inline-block", verticalAlign: "-2px", ...style }}
    aria-hidden="true"
  >
    {children}
  </svg>
);
const IconUsers = (p: IconProps) => (
  <Svg {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="3" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Svg>
);
const IconCalendar = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </Svg>
);
const IconDownload = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v12" />
    <path d="M7 10l5 5 5-5" />
    <path d="M5 21h14" />
  </Svg>
);
const IconFile = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 2H8a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </Svg>
);
const IconSun = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Svg>
);
const IconMoon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
  </Svg>
);
const IconLogout = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </Svg>
);
/* NEW: trash icon for Delete My Account */
const IconTrash = (p: IconProps) => (
  <Svg {...p}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </Svg>
);

/** Theme utils (local) */
function getTheme(): "light" | "dark" {
  return (localStorage.getItem("theme") as "light" | "dark") || "dark";
}
function setTheme(t: "light" | "dark") {
  localStorage.setItem("theme", t);
  if (typeof document !== "undefined") {
    document.body.classList.toggle("light-theme", t === "light");
  }
}

export default function FacultyDashboard() {
  const navigate = useNavigate();
  const loc = useLocation() as any;
  const college: string =
    loc?.state?.college ||
    (sessionStorage.getItem("college") ?? "UNKNOWN_COLLEGE");

  const [rows, setRows] = React.useState<StudentRow[]>([]);
  const [search, setSearch] = React.useState("");
  const [theme, setThemeState] = React.useState<"light" | "dark">(getTheme());

  // === Delete account modal state ===
  const [showDelete, setShowDelete] = React.useState(false);
  const [deleteTyping, setDeleteTyping] = React.useState("");
  const [deleteBusy, setDeleteBusy] = React.useState(false);
  const [deleteErr, setDeleteErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (college) sessionStorage.setItem("college", college);
    (async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      const root = ref(db);

      // Load users for this college
      const usersSnap = await get(child(root, `Colleges/${college}/Users`));
      const rolesSnap = await get(child(root, `UserRoles`));
      const roles: Record<string, string> = rolesSnap.exists()
        ? (rolesSnap.val() as Record<string, string>)
        : {};

      const list: StudentRow[] = [];
      if (usersSnap.exists()) {
        const usersObj: Record<
          string,
          { "Personal Data"?: { userName?: string; userEmail?: string } }
        > = usersSnap.val();

        for (const uid of Object.keys(usersObj)) {
          const pd = usersObj[uid]?.["Personal Data"] || {};
          const row: StudentRow = {
            uid,
            userName: pd?.userName,
            userEmail: pd?.userEmail,
          };

          const role = roles[uid];
          const looksLikeRealProfile = !!row.userName || !!row.userEmail;

          if (role === "student" || (!role && looksLikeRealProfile)) {
            list.push(row);
          }
        }

        list.sort((a, b) => {
          const aKey = (a.userName || a.userEmail || a.uid).toLowerCase();
          const bKey = (b.userName || b.userEmail || b.uid).toLowerCase();
          return aKey.localeCompare(bKey);
        });
      }
      setRows(list);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function logout() {
    auth.signOut();
    sessionStorage.removeItem("college");
    navigate("/login");
  }

  // Export CSV (Time Spent removed)
  function exportStudentsCSV(list: StudentRow[]) {
    const header = ["uid", "name", "email"];
    const lines = [header.join(",")];
    list.forEach((r) => {
      const line = [
        r.uid,
        (r.userName || "").replace(/,/g, " "),
        (r.userEmail || "").replace(/,/g, " "),
      ].join(",");
      lines.push(line);
    });
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `viscer_students_${college}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // Theme toggle action
  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setThemeState(next);
  }

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = (r.userName || "").toLowerCase();
      const email = (r.userEmail || "").toLowerCase();
      const uid = r.uid.toLowerCase();
      return name.includes(q) || email.includes(q) || uid.includes(q);
    });
  }, [rows, search]);

  // === Delete my account handlers (faculty/admin self-delete) ===
  async function handleDeleteMyAccount() {
    setShowDelete(true);
    setDeleteTyping("");
    setDeleteErr(null);
  }

  async function performDeletion() {
    if (deleteBusy) return;
    setDeleteBusy(true);
    setDeleteErr(null);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");
      const uid = user.uid;

      // 1) Firestore: delete my Scheduler bookings across all weeks
      const weeksCol = collection(fs, "scheduler", college, "weeks");
      const weeksSnap = await getDocs(weeksCol);
      for (const weekDoc of weeksSnap.docs) {
        const bookingsCol = collection(
          fs,
          "scheduler",
          college,
          "weeks",
          weekDoc.id,
          "bookings"
        );
        const bookSnap = await getDocs(bookingsCol);
        if (!bookSnap.empty) {
          const batch = writeBatch(fs);
          bookSnap.forEach((d: DocumentData) => {
            const data = d.data();
            if (data?.userId === uid) {
              batch.delete(
                fsDoc(
                  fs,
                  "scheduler",
                  college,
                  "weeks",
                  weekDoc.id,
                  "bookings",
                  d.id
                )
              );
            }
          });
          await batch.commit();
        }
      }

      // 2) Realtime DB: remove profile + mappings (works for both students & faculty/admins)
      const updates: Record<string, any> = {};
      updates[`UserToCollege/${uid}`] = null;
      updates[`UserRoles/${uid}`] = null;
      updates[`Colleges/${college}/Users/${uid}`] = null;
      updates[`Colleges/${college}/Notifications/${uid}`] = null;
      updates[`Users/${uid}`] = null; // if you keep a global profile node
      await update(ref(db), updates);

      // 3) Try to delete the Auth account
      try {
        await deleteUser(user);
      } catch (e: any) {
        // If recent login is required, we still removed their data.
        if (String(e?.code || "").includes("requires-recent-login")) {
          setDeleteErr(
            "Your data was deleted. Please sign in again to complete account removal if needed."
          );
        } else {
          setDeleteErr("Your data was deleted. Sign out completed.");
        }
      }

      // 4) Sign out and go to Login
      await auth.signOut();
      sessionStorage.removeItem("college");
      navigate("/login", { replace: true });
    } catch (e: any) {
      setDeleteErr(
        e?.message || "We couldn't complete deletion. Please try again."
      );
      setDeleteBusy(false);
    }
  }

  return (
    <div className="container">
      {/* Header with logo, title, actions */}
      <div className="row" style={{ marginBottom: 12, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src="/viscer-logo.svg"
            alt="VisceR"
            height={50}
            style={{ display: "block" }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = "/viscer-logo.png";
            }}
          />
          <h2 style={{ margin: 0 }}>Faculty/Admin Dashboard</h2>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            className="btn"
            onClick={() => navigate("/f/groups", { state: { college } })}
          >
            <IconUsers /> <span style={{ marginLeft: 6 }}>Create Cohort</span>
          </button>

          {/* Scheduler */}
          <button
            className="btn"
            onClick={() => navigate("/f/scheduler", { state: { college } })}
            title="Open Scheduler"
          >
            <IconCalendar /> <span style={{ marginLeft: 6 }}>Scheduler</span>
          </button>

          <button className="btn" onClick={() => exportStudentsCSV(filtered)}>
            <IconDownload /> <span style={{ marginLeft: 6 }}>Export CSV</span>
          </button>
          <button className="btn" onClick={() => window.print()}>
            <IconFile /> <span style={{ marginLeft: 6 }}>Export PDF</span>
          </button>

          {/* (MOVED) Delete button removed from header */}
          <button
            className="btn"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? "Light" : "Dark"}
          </button>
          <button className="btn" onClick={logout}>
            <IconLogout /> <span style={{ marginLeft: 6 }}>Log out</span>
          </button>
        </div>
      </div>

      {/* Search + (NEW) Delete button inside this card */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div
          className="row"
          style={{ alignItems: "center", gap: 12, flexWrap: "wrap" }}
        >
          <input
            className="input"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: "1 1 360px", minWidth: 280, maxWidth: 560 }}
          />

          {/* NEW: Delete My Account moved here */}
          <button
            className="btn"
            onClick={handleDeleteMyAccount}
            title="Delete my account and data"
            style={{
              borderColor: "rgba(239,68,68,.5)",
              background: "rgba(239,68,68,.08)",
              marginLeft: "auto",
            }}
          >
            <IconTrash />{" "}
            <span style={{ marginLeft: 6 }}>Delete My Account</span>
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDelete && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.5)",
            display: "grid",
            placeItems: "center",
            zIndex: 9999,
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="card"
            style={{
              maxWidth: 520,
              borderColor: "rgba(239,68,68,.5)",
              background: "var(--surface-1)",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Delete My Account</h3>
            <p className="small" style={{ marginTop: 6 }}>
              This will permanently delete your VisceR account and personal
              data: your profile, college mappings, notifications, and your
              headset booking history. This action cannot be undone.
            </p>
            <p className="small" style={{ marginTop: 6 }}>
              To confirm, type <b>DELETE</b> below and press{" "}
              <b>Confirm &amp; Delete</b>.
            </p>
            <input
              className="input"
              placeholder="Type DELETE to confirm"
              value={deleteTyping}
              onChange={(e) => setDeleteTyping(e.target.value)}
              style={{ marginTop: 8 }}
            />
            {deleteErr && (
              <p className="small" style={{ color: "#ff6b6b", marginTop: 8 }}>
                {deleteErr}
              </p>
            )}
            <div className="row" style={{ marginTop: 12, gap: 8 }}>
              <button
                className="btn"
                onClick={() => setShowDelete(false)}
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={performDeletion}
                disabled={deleteTyping !== "DELETE" || deleteBusy}
                style={{
                  borderColor: "rgba(239,68,68,.6)",
                  background: "rgba(239,68,68,.12)",
                }}
              >
                {deleteBusy ? "Deleting…" : "Confirm & Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Students table */}
      <section className="card">
        <h3>Students in {college}</h3>
        {filtered.length === 0 ? (
          <p className="small">No matching students.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: "30%" }}>Name</th>
                <th style={{ width: "50%" }}>Email</th>
                <th style={{ width: "20%" }}>Open</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.uid}>
                  <td>{r.userName || "—"}</td>
                  <td>{r.userEmail || "—"}</td>
                  <td>
                    <Link to={`/student/${r.uid}`} state={{ college }}>
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
